/**
 * segmentReordering.ts - Utilities for reordering segment nodes within a switch
 *
 * Segment nodes are children of switch nodes and appear horizontally.
 * This module provides functions to reorder segments while ensuring
 * the default segment always stays rightmost.
 *
 * Related:
 *   - utils/nodeReordering.ts - General node reordering
 *   - nodes/SegmentNode.tsx - Segment node component
 */

import type { Edge, Node } from '@xyflow/react';
import type { BaseNodeData } from '../types';
import type { SegmentNodeData } from '../types/stepNodes';
import { NODE_TYPES } from '../types';

/**
 * Get all segment nodes for a given switch node, ordered by segmentIndex
 *
 * Note: Uses the segment's parentSwitchNodeId data property to find segments,
 * not edge sources. This is because branch edges use T-junction visual style
 * where the source is the step BEFORE the switch, not the switch itself.
 */
export function getOrderedSegments(
	parentSwitchNodeId: string,
	nodes: Node<BaseNodeData>[],
	_edges: Edge[]
): Node<SegmentNodeData>[] {
	// Find segment nodes by their parentSwitchNodeId data property
	const segmentNodes = nodes.filter(
		n => n.type === NODE_TYPES.SEGMENT && (n.data as SegmentNodeData).parentSwitchNodeId === parentSwitchNodeId
	) as Node<SegmentNodeData>[];

	// Sort by segmentIndex
	return segmentNodes.sort((a, b) => (a.data.segmentIndex ?? 0) - (b.data.segmentIndex ?? 0));
}
